'use client';

import { useContext, useEffect, useState, useRef, type ReactNode } from 'react';
import Link from 'next/link';
import { FiArrowDownRight, FiArrowUpRight, FiLink } from 'react-icons/fi';
import { TbArrowsUpDown, TbChartLine } from 'react-icons/tb';
import { HiOutlineExclamationCircle } from 'react-icons/hi2';
import { SessionContext } from '@benzinga/session-context';
import { useWatchlistContext } from '@benzinga/user-context';
import { formatTickerToURLFriendly } from '@benzinga/utils';

import { getWatchlistDailyChangePercent } from '../data';
import { formatHoldingCurrency, formatPortfolioCurrency } from './portfolioFormatters';
import type { DailyChangeResult, PortfolioHoldingValues, SectorAllocation } from './portfolioActionUtils';
import {
  AllocationBarVisual,
  AllocationLoadingBody,
  AllocationPreviewBody,
  HEADER_ALLOCATION_PANEL_CLASSNAME,
  HEADER_CHIP_ROW_CLASSNAME,
  HeaderCardBody,
  HeaderCardEmptySkeleton,
  HeaderCardMetricSkeleton,
  HeaderCardMoversSkeleton,
  HeaderCardTodaySkeleton,
  HeaderChip,
  HeaderChipRowSkeleton,
  PREVIEW_ALLOCATIONS,
} from './portfolioHeaderSkeletons';
import { shouldShowHeaderAllocationSkeleton } from './portfolioPageShell';
import { usePortfolioQuoteBar } from './usePortfolioQuoteBar';
import { TickerIdentity } from '../../../edge/dashboard/_components/TickerLogo';
import {
  HeaderCard,
  HeaderCardEmpty,
  HeaderCardList,
  HeaderCardRow,
  HeaderChrome,
  HeaderOverviewCards,
  HeaderPageTitle,
  HeaderTickerPrice,
} from '../../../edge/dashboard/_components/HeaderChrome';

interface PortfolioPageHeaderProps {
  allocations: SectorAllocation[];
  hasHoldings: boolean;
  holdingValues: PortfolioHoldingValues;
  isLoadingPrices: boolean;
  isLoadingSectors: boolean;
}

const changeColor = (value: number) => (value >= 0 ? 'text-bzgreen-500' : 'text-bzred-500');

const formatSignedPercent = (value: number) => `${value >= 0 ? '+' : '−'}${Math.abs(value).toFixed(2)}%`;

const ChangeDirectionBox = ({ value }: { value: number }) => {
  const isUp = value >= 0;
  const Icon = isUp ? FiArrowUpRight : FiArrowDownRight;

  return (
    <span
      aria-hidden
      className={`inline-flex size-11 shrink-0 items-center justify-center rounded-lg text-base ${
        isUp ? 'bg-bzgreen-500/15 text-bzgreen-500' : 'bg-bzred-500/15 text-bzred-500'
      }`}
    >
      <Icon size={30} strokeWidth={2.5} />
    </span>
  );
};

const tickerCountPhrase = (count: number, singular: string, plural: string) =>
  `${count} ${count === 1 ? singular : plural}`;

const BreadthBar = ({ downCount, upCount }: { downCount: number; upCount: number }) => {
  const moverCount = downCount + upCount;
  const losersLabel = tickerCountPhrase(downCount, 'loser', 'losers');
  const gainersLabel = tickerCountPhrase(upCount, 'gainer', 'gainers');

  return (
    <div aria-label={`${losersLabel}, ${gainersLabel}`} className="flex flex-col gap-1.5" role="status">
      <div aria-hidden className="flex h-2.5 w-full overflow-hidden rounded-full bg-white/10">
        {downCount > 0 ? (
          <span className="h-full min-w-0.5" style={{ backgroundColor: '#FF4050', flex: downCount }} />
        ) : null}
        {upCount > 0 ? (
          <span className="h-full min-w-0.5" style={{ backgroundColor: '#30BF60', flex: upCount }} />
        ) : null}
        {moverCount === 0 ? <span className="h-full flex-1 bg-white/10" /> : null}
      </div>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs font-semibold tabular-nums leading-none">
        <p className="text-bzred-500">{losersLabel}</p>
        <p className="text-bzgreen-500">{gainersLabel}</p>
      </div>
    </div>
  );
};

export const TodayMoveBody = ({ dailyChange }: { dailyChange: DailyChangeResult }) => {
  const leader = dailyChange.leader;
  const leaderLabel = dailyChange.weighting === 'portfolio' ? 'Biggest impact on value' : 'Largest % move';

  return (
    <div className="flex h-full flex-col justify-center gap-2.5 px-4 py-3">
      <div className="flex min-h-0 items-start justify-between gap-3">
        <div className="min-w-0">
          <p
            className={`flex items-center gap-3 text-4xl font-bold leading-none tracking-tight tabular-nums ${changeColor(dailyChange.percent)}`}
          >
            <ChangeDirectionBox value={dailyChange.percent} />
            {formatSignedPercent(dailyChange.percent)}
          </p>
          <p className="mt-2 text-sm leading-snug text-bzgray-500">
            {dailyChange.weighting === 'portfolio' ? 'Weighted by holding size' : 'Average of all symbols'}
          </p>
        </div>
        {leader ? (
          <Link
            className="flex min-w-0 shrink-0 flex-col items-end text-right hover:underline"
            href={`/quote/${formatTickerToURLFriendly(leader.symbol)}`}
            prefetch={false}
          >
            <span className="text-[11px] font-medium leading-none text-bzgray-500">{leaderLabel}</span>
            <span className="mt-1 text-sm font-bold leading-none text-white">{leader.symbol}</span>
            <span className={`mt-1 text-sm font-semibold tabular-nums ${changeColor(leader.changePercent)}`}>
              {formatSignedPercent(leader.changePercent)}
            </span>
          </Link>
        ) : null}
      </div>
      <BreadthBar downCount={dailyChange.downCount} upCount={dailyChange.upCount} />
    </div>
  );
};

const AllocationPanel = ({
  allocations,
  isLoading,
  onLinkBrokerage,
  showPreview,
}: {
  allocations: SectorAllocation[];
  isLoading: boolean;
  onLinkBrokerage: () => void;
  showPreview: boolean;
}) => {
  const liveTotal = allocations.reduce((total, allocation) => total + allocation.value, 0);
  const isPreview = showPreview && !isLoading && liveTotal <= 0;
  const displayAllocations = liveTotal > 0 ? allocations : PREVIEW_ALLOCATIONS;

  return (
    <div className={HEADER_ALLOCATION_PANEL_CLASSNAME}>
      {isLoading ? (
        <AllocationLoadingBody />
      ) : isPreview ? (
        <AllocationPreviewBody onUnlock={onLinkBrokerage} />
      ) : (
        <AllocationBarVisual allocations={displayAllocations} isPreview={false} />
      )}
    </div>
  );
};

export const PortfolioPageHeader = ({
  allocations,
  hasHoldings,
  holdingValues,
  isLoadingPrices,
  isLoadingSectors,
}: PortfolioPageHeaderProps) => {
  const { isLoadingWatchlists, overviewWatchlist, setShowCreateModal, setShowYodleeModal, watchlists } =
    useWatchlistContext();
  const session = useContext(SessionContext);
  const [dailyChange, setDailyChange] = useState<DailyChangeResult | null>(null);
  const [isLoadingDailyChange, setIsLoadingDailyChange] = useState(true);
  const { isLoadingMovers, movers } = usePortfolioQuoteBar();

  const watchlistCount = watchlists.length;
  const hasWatchlists = watchlistCount > 0;
  const symbolCount = overviewWatchlist?.symbols.length ?? 0;
  const overviewWatchlistId = overviewWatchlist?.watchlistId;
  const overviewSymbolsKey = overviewWatchlist?.symbols.map(symbol => symbol.symbol).join(',') ?? '';
  const overviewWatchlistRef = useRef(overviewWatchlist);
  overviewWatchlistRef.current = overviewWatchlist;

  useEffect(() => {
    const abortController = new AbortController();
    const watchlist = overviewWatchlistRef.current;

    const loadDailyChange = async () => {
      if (!watchlist || watchlist.symbols.length === 0) {
        setDailyChange(null);
        setIsLoadingDailyChange(false);
        return;
      }

      setIsLoadingDailyChange(true);
      const nextDailyChange = await getWatchlistDailyChangePercent(watchlist, session.getSession());
      if (abortController.signal.aborted) return;
      setDailyChange(nextDailyChange);
      setIsLoadingDailyChange(false);
    };

    loadDailyChange();
    return () => abortController.abort();
  }, [overviewWatchlistId, overviewSymbolsKey, session]);

  const scrollToWatchlists = () => {
    document.getElementById('portfolio-watchlists')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const openCreateWatchlist = () => {
    setShowCreateModal(true);
  };

  const watchlistCardFooter = hasWatchlists
    ? { footer: 'Jump to watchlists', footerDisabled: isLoadingWatchlists, onFooterClick: scrollToWatchlists }
    : { footer: 'Create watchlist', footerDisabled: isLoadingWatchlists, onFooterClick: openCreateWatchlist };

  const moversCardFooter = hasWatchlists
    ? { footer: 'View all symbols', footerDisabled: isLoadingWatchlists, onFooterClick: scrollToWatchlists }
    : { footer: 'Create watchlist', footerDisabled: isLoadingWatchlists, onFooterClick: openCreateWatchlist };

  const { costBasis, totalValue } = holdingValues;
  const allTimeGain = totalValue - costBasis;
  const allTimePct = costBasis > 0 ? (allTimeGain / costBasis) * 100 : 0;
  const showBrokerageSkeleton = isLoadingWatchlists || (hasHoldings && isLoadingPrices);
  const showAllocationSkeleton = shouldShowHeaderAllocationSkeleton(
    isLoadingWatchlists,
    hasHoldings,
    isLoadingPrices,
    isLoadingSectors,
    allocations.length,
  );
  const showDailySkeleton = isLoadingWatchlists || isLoadingDailyChange;
  const brokerageFooter = isLoadingWatchlists || hasHoldings ? 'Sync brokerage' : 'Link brokerage';
  const todayCardTitle =
    !showDailySkeleton && dailyChange?.weighting === 'portfolio' ? 'Portfolio today' : 'Watchlists today';

  let moversBody: ReactNode;
  if (isLoadingMovers) {
    moversBody = <HeaderCardMoversSkeleton />;
  } else if (movers.length > 0) {
    moversBody = (
      <HeaderCardList>
        {movers.map(mover => {
          const isUp = mover.changePercent >= 0;
          return (
            <HeaderCardRow key={mover.symbol}>
              <Link className="flex min-w-0 overflow-hidden" href={`/quote/${formatTickerToURLFriendly(mover.symbol)}`} prefetch={false}>
                <TickerIdentity logo={mover.logo} name={mover.name} symbol={mover.symbol} tone="dark" />
              </Link>
              <HeaderTickerPrice
                percent={formatSignedPercent(mover.changePercent)}
                positive={isUp}
                price={formatHoldingCurrency(mover.lastTradePrice)}
              />
            </HeaderCardRow>
          );
        })}
      </HeaderCardList>
    );
  } else if (!hasWatchlists) {
    moversBody = (
      <HeaderCardEmpty
        description="Create a watchlist to start tracking movers."
        icon={TbArrowsUpDown}
        title="See what's moving"
      />
    );
  } else if (symbolCount === 0) {
    moversBody = (
      <HeaderCardEmpty
        description="Add tickers to see your biggest gainers and losers."
        icon={TbArrowsUpDown}
        title="See what's moving"
      />
    );
  } else {
    moversBody = (
      <HeaderCardEmpty
        description="Live quotes aren't available right now. Check back shortly."
        icon={HiOutlineExclamationCircle}
        title="Quotes unavailable"
      />
    );
  }

  return (
    <HeaderChrome>
      <div>
        <HeaderPageTitle>Portfolio & Watchlists</HeaderPageTitle>
        <div className={HEADER_CHIP_ROW_CLASSNAME} aria-busy={isLoadingWatchlists}>
          {isLoadingWatchlists ? (
            <HeaderChipRowSkeleton />
          ) : (
            <>
              <HeaderChip>
                {watchlistCount} {watchlistCount === 1 ? 'watchlist' : 'watchlists'}
              </HeaderChip>
              <HeaderChip>
                {symbolCount} {symbolCount === 1 ? 'symbol' : 'symbols'}
              </HeaderChip>
            </>
          )}
        </div>
      </div>

      <HeaderOverviewCards className="mt-2 sm:mt-3">
        <HeaderCard title={todayCardTitle} isLoadingFooter={isLoadingWatchlists} {...watchlistCardFooter}>
          {showDailySkeleton ? (
            <HeaderCardTodaySkeleton />
          ) : dailyChange ? (
            <TodayMoveBody dailyChange={dailyChange} />
          ) : !hasWatchlists ? (
            <HeaderCardEmpty
              description="Create a watchlist to start tracking today's move."
              icon={TbChartLine}
              title="Track today's move"
            />
          ) : symbolCount === 0 ? (
            <HeaderCardEmpty
              description="Add tickers to your watchlist to see today's move."
              icon={TbChartLine}
              title="Track today's move"
            />
          ) : (
            <HeaderCardEmpty
              description="Live quotes aren't available right now. Check back shortly."
              icon={HiOutlineExclamationCircle}
              title="Quotes unavailable"
            />
          )}
        </HeaderCard>

        <HeaderCard title="Top movers" isLoadingFooter={isLoadingWatchlists} {...moversCardFooter}>
          {moversBody}
        </HeaderCard>

        <HeaderCard
          title="Brokerage"
          footer={brokerageFooter}
          isLoadingFooter={isLoadingWatchlists}
          onFooterClick={() => setShowYodleeModal(true)}
        >
          {showBrokerageSkeleton ? (
            hasHoldings ? (
              <HeaderCardMetricSkeleton />
            ) : (
              <HeaderCardEmptySkeleton />
            )
          ) : hasHoldings ? (
            <HeaderCardBody>
              <p className="text-4xl font-bold leading-none tracking-tight text-white">
                {formatPortfolioCurrency(totalValue)}
              </p>
              <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
                <span
                  className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold tabular-nums leading-none ${
                    allTimeGain >= 0 ? 'bg-bzgreen-500/10 text-bzgreen-500' : 'bg-bzred-500/10 text-bzred-500'
                  }`}
                >
                  {allTimeGain >= 0 ? '+' : '−'}
                  {formatPortfolioCurrency(Math.abs(allTimeGain))}
                </span>
                <span
                  className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold tabular-nums leading-none ${
                    allTimeGain >= 0 ? 'bg-bzgreen-500/10 text-bzgreen-500' : 'bg-bzred-500/10 text-bzred-500'
                  }`}
                >
                  {formatSignedPercent(allTimePct)} all-time
                </span>
              </div>
              <p className="mt-2 text-sm leading-snug text-bzgray-500">
                Cost basis {formatPortfolioCurrency(costBasis)}
              </p>
            </HeaderCardBody>
          ) : (
            <HeaderCardEmpty
              description="Connect an account to see value, allocation, and holdings."
              icon={FiLink}
              title="No brokerage linked"
            />
          )}
        </HeaderCard>
      </HeaderOverviewCards>

      <AllocationPanel
        allocations={allocations}
        isLoading={showAllocationSkeleton}
        onLinkBrokerage={() => setShowYodleeModal(true)}
        showPreview={!hasHoldings}
      />
    </HeaderChrome>
  );
};
